import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import {
  CalendarBucket,
  CalendarDayCount,
  fetchDueCounts,
} from "../../../services/calendarService";
import CalendarDayModal from "./CalendarDayModal";
import Toast, { type ToastMessage } from "../../../components/common/Toast";

interface Leave {
  id: number;
  type: string;
  from: string;
  to: string;
  days: number;
  status: string;
  reason: string;
}

interface PlanCalendarNetBodyProps {
  leaves: Leave[];
}

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const pad = (n: number) => String(n).padStart(2, "0");
const isoFromYMD = (y: number, m: number, d: number) =>
  `${y}-${pad(m + 1)}-${pad(d)}`;

// Bucket -> semantic chip styles using the shared status-badge palette.
const CHIP_STYLES: Record<CalendarBucket, string> = {
  due: "status-badge xs warning",
  overdue: "status-badge xs danger",
  upcoming: "status-badge xs info",
  completed: "status-badge xs success",
};

const CHIP_LABEL: Record<CalendarBucket, string> = {
  due: "D",
  overdue: "O",
  upcoming: "U",
  completed: "C",
};

const PlanCalendarNetBody: React.FC<PlanCalendarNetBodyProps> = ({ leaves }) => {
  const today = useMemo(() => new Date(), []);
  const [cursor, setCursor] = useState<{ year: number; month: number }>(() => ({
    year: today.getFullYear(),
    month: today.getMonth(),
  }));
  const [counts, setCounts] = useState<Record<string, CalendarDayCount>>({});
  const [serverToday, setServerToday] = useState<string>(
    isoFromYMD(today.getFullYear(), today.getMonth(), today.getDate()),
  );
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const [modal, setModal] = useState<{
    date: Date;
    initialBucket: CalendarBucket;
    availableBuckets: CalendarBucket[];
  } | null>(null);

  const monthStart = useMemo(
    () => new Date(cursor.year, cursor.month, 1),
    [cursor],
  );
  const monthEnd = useMemo(
    () => new Date(cursor.year, cursor.month + 1, 0),
    [cursor],
  );
  const daysInMonth = monthEnd.getDate();
  // Monday-first grid: shift JS getDay() (0=Sun) so Mon=0 … Sun=6.
  const startDayOffset = (monthStart.getDay() + 6) % 7;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setToast(null);
    fetchDueCounts(monthStart, monthEnd)
      .then((resp) => {
        if (cancelled) return;
        const map: Record<string, CalendarDayCount> = {};
        for (const d of resp.data) map[d.date] = d;
        setCounts(map);
        if (resp.today) setServerToday(resp.today);
      })
      .catch((e) => {
        if (!cancelled) setToast({ type: "error", message: e?.message || "Failed to load calendar" });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [monthStart, monthEnd]);

  // Single month-stepper used by both the arrow buttons and the horizontal
  // wheel/swipe gesture. Functional update keeps it stable (no stale cursor).
  const stepMonth = useCallback((dir: number) => {
    setCursor((c) => {
      let m = c.month + dir;
      let y = c.year;
      if (m < 0) { m = 11; y -= 1; }
      else if (m > 11) { m = 0; y += 1; }
      return { year: y, month: m };
    });
  }, []);
  const goPrev = () => stepMonth(-1);
  const goNext = () => stepMonth(1);

  // Horizontal scroll (trackpad swipe, or Shift+wheel) flips months. The grid
  // itself never scrolls — the whole month is always visible. A cooldown keeps
  // one gesture from skipping several months at once. Native non-passive
  // listener so we can preventDefault the browser's back-swipe / page scroll.
  const gridRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    let locked = false;
    const onWheel = (e: WheelEvent) => {
      const horizontal = Math.abs(e.deltaX) >= Math.abs(e.deltaY);
      const delta = horizontal ? e.deltaX : (e.shiftKey ? e.deltaY : 0);
      if (!delta) return;              // pure vertical, no Shift → ignore
      e.preventDefault();
      if (locked) return;
      locked = true;
      stepMonth(delta > 0 ? 1 : -1);
      setTimeout(() => { locked = false; }, 350);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [stepMonth]);

  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const blanks = Array.from({ length: startDayOffset }, (_, i) => i);

  const isToday = (day: number) =>
    cursor.year === today.getFullYear() &&
    cursor.month === today.getMonth() &&
    day === today.getDate();

  const openModal = (
    day: number,
    bucket: CalendarBucket,
    available: CalendarBucket[],
  ) => {
    setModal({
      date: new Date(cursor.year, cursor.month, day),
      initialBucket: bucket,
      availableBuckets: available,
    });
  };

  return (
    <div className="netbody-card flex flex-col h-full min-h-0 overflow-hidden">
      <div className="flex items-center justify-between netbody-header">
        {/* Prev — left edge */}
        <button type="button" onClick={goPrev} className="p-1 hover:bg-gray-100 rounded" aria-label="Previous month">
          <ChevronLeft className="w-5 h-5 text-gray-500" />
        </button>
        {/* Center: Month + Year */}
        <div className="flex items-center gap-2">
          <h3 className="font-bold text-gray-800 text-lg">
            {MONTH_NAMES[cursor.month]} {cursor.year}
          </h3>
          {loading && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
        </div>
        {/* Next — right edge */}
        <button type="button" onClick={goNext} className="p-1 hover:bg-gray-100 rounded" aria-label="Next month">
          <ChevronRight className="w-5 h-5 text-gray-500" />
        </button>
      </div>

      {/* grey rule under the header — same colour/weight as the inbox toolbar
          rule and the vertical pane separator, so the line continues across. */}
      <div aria-hidden="true" style={{ height: "1px", flexShrink: 0 }} className="bg-gray-200" />

      <div className="netbody-content flex-1 min-h-0 flex flex-col">
        <div ref={gridRef} className="flex-1 min-h-0 flex flex-col overflow-hidden">
          <div className="grid grid-cols-7 bg-gray-50">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
              <div key={d} className="p-3 text-center text-xs font-bold text-gray-400 uppercase">
                {d}
              </div>
            ))}
          </div>
          {/* grey rule under the weekday labels — full-bleed (cancels netbody-content's
              10px side padding) so it spans the full pane width and lines up with the
              inbox toolbar rule across both panes. */}
          <div aria-hidden="true" className="bg-gray-200 mb-2" style={{ height: "1px", marginLeft: "-10px", marginRight: "-10px", flexShrink: 0 }} />

          <div className="grid grid-cols-7 gap-1.5 p-2 flex-1 min-h-0" style={{ gridAutoRows: "1fr" }}>
            {blanks.map((_, i) => (
              <div key={`blank-${i}`} className="bg-gray-50/30 rounded-lg min-h-0"></div>
            ))}
            {days.map((day) => {
              const dateStr = isoFromYMD(cursor.year, cursor.month, day);
              const dayData = counts[dateStr];
              const leave = leaves.find((l) => l.from === dateStr || l.to === dateStr);
              const todayCell = dateStr === serverToday || isToday(day);
              const isPast = dateStr < serverToday;
              const isFuture = dateStr > serverToday;

              const due = dayData?.due ?? 0;
              const overdue = todayCell ? (dayData?.overdue ?? 0) : 0;
              const upcoming = !isPast ? (dayData?.upcoming ?? 0) : 0;
              const completed = !isFuture ? (dayData?.completed ?? 0) : 0;

              // Bucket availability per the spec:
              //   Today   → Overdue + Due + Upcoming + Completed
              //   Future  → Due + Upcoming  (each cell's own 7-day rolling window)
              //   Past    → Due + Completed
              const available: CalendarBucket[] = [];
              if (todayCell) {
                available.push("due", "overdue", "upcoming", "completed");
              } else if (isPast) {
                available.push("due", "completed");
              } else {
                available.push("due", "upcoming");
              }

              // Click target chooses initial tab; cell click defaults to first
              // non-zero metric.
              const firstNonZero =
                (overdue > 0 && "overdue") ||
                (due > 0 && "due") ||
                (upcoming > 0 && "upcoming") ||
                (completed > 0 && "completed") ||
                null;
              const clickable = firstNonZero !== null && available.length > 0;

              // Each metric chip is absolutely positioned at a FIXED corner so it
              // always lands in the same spot regardless of which others show:
              //   overdue   → top-right (today only)
              //   completed → dead centre of the cell (past/today only)
              //   upcoming  → bottom-left
              //   due       → bottom-right
              const chip = (
                bucket: CalendarBucket,
                count: number,
                title: string,
                posCls: string,
              ) =>
                count > 0 ? (
                  <button
                    type="button"
                    data-testid={`cal-chip-${dateStr}-${bucket}`}
                    onClick={(e) => { e.stopPropagation(); openModal(day, bucket, available); }}
                    className={`absolute ${posCls} text-[11px] font-semibold px-1.5 py-0.5 rounded ${CHIP_STYLES[bucket]}`}
                    title={title}
                  >
                    {CHIP_LABEL[bucket]} {count}
                  </button>
                ) : null;

              return (
                <div
                  key={day}
                  data-testid={`cal-cell-${dateStr}`}
                  className={`relative border rounded-lg min-h-0 p-2 transition-all ${
                    clickable
                      ? "cursor-pointer hover:shadow-md hover:border-purple-200"
                      : "cursor-default"
                  } ${todayCell ? "border-purple-300 bg-purple-50/30" : "border-gray-100"}`}
                  onClick={
                    clickable
                      ? () => openModal(day, firstNonZero as CalendarBucket, available)
                      : undefined
                  }
                >
                  {/* Day number */}
                  <span
                    className={`absolute top-2 left-2 text-sm font-semibold ${
                      todayCell ? "text-purple-700" : "text-gray-700"
                    }`}
                  >
                    {day}
                  </span>

                  {/* Leave chip — top-right */}
                  {leave && (
                    <span className="absolute top-2 right-2 status-badge xs info">
                      {leave.type}
                    </span>
                  )}

                  {/* Metric chips — fixed corners (see `chip` above). */}
                  {chip("overdue", overdue, `${overdue} overdue as of today`, "top-2 right-2")}
                  {chip("completed", completed, `${completed} completed on ${dateStr}`, "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2")}
                  {chip("upcoming", upcoming, `${upcoming} upcoming in next 7 days`, "bottom-2 left-2")}
                  {chip("due", due, `${due} due on ${dateStr}`, "bottom-2 right-2")}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <CalendarDayModal
        isOpen={modal !== null}
        onClose={() => setModal(null)}
        date={modal?.date ?? null}
        initialBucket={modal?.initialBucket ?? "due"}
        availableBuckets={modal?.availableBuckets ?? ["due"]}
      />
      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
};

export default PlanCalendarNetBody;
